import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { safeJsonStringify } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { LoginService } from '@ebw-monorepo/login';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import { MtxGrid, MtxGridColumn, MtxGridRowClassFormatter } from '@ng-matero/extensions/grid';
import { TranslateService } from '@ngx-translate/core';
import { IMapTable } from 'libs/ng-components/map/src/lib/interfaces/mapTable.interface';
import { FeatureSelectService } from 'libs/ng-components/map/src/lib/services/feature-select.service';
import { LayerStylesService } from 'libs/ng-components/map/src/lib/services/layer-styles.service';
import { MapTableService } from 'libs/ng-components/map/src/lib/services/map-table.service';
import { MapService } from 'libs/ng-components/map/src/lib/services/map.service';
import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import moment from 'moment';
import { Feature } from 'ol';
import { click } from 'ol/events/condition';
import { GeoJSON } from 'ol/format';
import { Select } from 'ol/interaction';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { AttivitaStatus } from '../checkin-out.component';

type ApiResponseModel = {
  numRows: number;
  records: any[];
  error: 1 | 0;
  message: string;
  status: number;
  traceback: any;
  path: any;
  buffer: any;
};

@Component({
  selector: 'ebw-monorepo-checkin-out-table',
  templateUrl: './checkin-out-table.component.html',
  styleUrls: ['./checkin-out-table.component.scss']
})
export class CheckinOutTableComponent implements OnInit, OnDestroy {
  @ViewChild('grid') public gridTable: MtxGrid;
  @ViewChild('geometryColTemplate', { static: true })
  geometryColTemplate!: TemplateRef<any>;

  // template reference to render the ID column
  @ViewChild('idColTemplate', { static: true })
  idColTemplate!: TemplateRef<any>;

  // template reference to render the ID Feature column
  @ViewChild('idFeatureColTemplate', { static: true })
  idFeatureColTemplate!: TemplateRef<any>;

  // template reference to render the info column
  @ViewChild('infoColTemplate', { static: true })
  infoColTemplate!: TemplateRef<any>;

  // --------------------------------------------------------------------------------------------------------------------

  // --------------------------------------------------------------------------------------------------------------------
  public dataGrid = [];
  public columnsGrid: MtxGridColumn[] = [];
  public pageSize = 50;
  public pageSizeOptions = [50, 100, 500];
  public currentPageIndex = 0;
  public offset = 0;
  public totalCount = 0;
  public config: IMapTable = null;

  private _destroy$ = new Subject<void>();
  private _currentSort: { field: string; direction: number };
  private _fwLinkFeatureStyle;
  private _fwBufferFeatureStyle;
  private _collectionConfig;

  private _headers;
  private _selectClickInteractionKey = 'CHECKINOUT_CLICK_INTERACTION';
  private _clickOwnerId = 'checkin-out-table';
  private _clickInteractionActive = false;
  private _selectClickInteraction: Select;

  public rowClassFormatter: MtxGridRowClassFormatter = {
    highlighted: (data, index) => {
      return data.highlighted;
    }
  };

  constructor(
    private mapTableSrv: MapTableService,
    private mapService: MapService,
    private httpClient: HttpClient,
    private ebwModal: EbwModalService,
    private i18NSrv: I18NService,
    private translateService: TranslateService,
    private _layerStylesService: LayerStylesService,
    private _utilsService: UtilsService,
    private _featureSelectService: FeatureSelectService,
    private _apiUrlService: ApiUrlService
  ) {}

  // --------------------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this._collectionConfig = this._featureSelectService.getCollectionConfig();

    const url = this._apiUrlService.createUrl(API_URLS.USER.COMPANY.GET_USER_COMPANY);
    this._utilsService.getRequest(`${url}?user_id=${sessionStorage.getItem(LoginService.USER_ID_KEY)}`).subscribe({
      next: (value) => {
        this.initializeTable(value.owner);
      },
      error: (err) => {
        this.ebwModal.openErrorModal({
          title: this.i18NSrv.getInstantValue('common.error'),
          content: err?.error?.message ?? 'Errore recupero azienda collegata'
        });
        this.initializeTable(false);
      }
    });

    this.mapTableSrv
      .getMapTableConfig()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (resp) => {
          this.config = resp;
          this._currentSort = null;

          this._headers = new HttpHeaders({
            Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY)
          });

          this.getData();
        },
        error: (err) => {
          console.error(err);
        }
      });

    this._fwLinkFeatureStyle = this._layerStylesService.buildFeatureStyleFromId('FWLinkStyle');
    this._fwBufferFeatureStyle = this._layerStylesService.buildFeatureStyleFromId('FWBufferStyle');

    this._acquireClickOwner();
  }

  // --------------------------------------------------------------------------------------------------------------------

  ngOnDestroy() {
    this.closeMapTable();
    // il flag va spento prima di rilasciare il registro (un altro passive in attesa lo riaccende nel suo onResume)
    this._removeSelectFeatureClickIteration();
    this.mapService.releaseMapClick(this._clickOwnerId);
    this._destroy$.next();
  }

  // --------------------------------------------------------------------------------------------------------------------

  closeMapTable() {
    this.mapTableSrv.setMapTableConfig(null);
    this.mapService.clearAllFeaturesFromTemporaryLayer();
    this.mapService.clearAllFeaturesFromTemporaryLayer(true);
  }

  // --------------------------------------------------------------------------------------------------------------------
  public onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPageIndex = event.pageIndex;
    this.offset = event.pageIndex * this.pageSize;

    this.getData();
  }

  public onSortChange(event: Sort) {
    if (event.direction !== '') {
      this._currentSort = {
        field: event.active,
        direction: event.direction === 'asc' ? -1 : 1
      };
    } else {
      this._currentSort = null;
    }
    this.getData();
  }

  // --------------------------------------------------------------------------------------------------------------------
  /**
   * function to execute a query and obtain its data
   */
  private getData() {
    this.resetComponentProperties();
    // setup the required headers
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY)
    });
    let url = this._apiUrlService.createUrl(API_URLS.CHECKIN.LIST); //+ this.buildQueryParameters(); // TODO buildQueryParameters non esiste, l'ho commentato

    let filters = this.getFilters();
    // MOCK SERVER
    // url = 'http://localhost:3100/attivita/checkin/list';
    this.httpClient.post(url, filters, { headers: this._headers }).subscribe({
      next: (resp: ApiResponseModel) => {
        this.totalCount = resp.numRows;

        resp.records.forEach((record) => {
          record.highlighted = false;
          if (record.position) {
            let styleId;
            if (record.type == 'area') {
              //area
              switch (record.status) {
                case AttivitaStatus.OPEN:
                  styleId = 'CheckInMarkerAreaRed';
                  break;
                case AttivitaStatus.EXPIRED:
                case AttivitaStatus.CLOSED:
                  styleId = 'CheckInMarkerAreaGreen';
                  break;
              }
            } else {
              //punto
              switch (record.status) {
                case AttivitaStatus.OPEN:
                  styleId = 'CheckInMarkerRed';
                  break;
                case AttivitaStatus.EXPIRED:
                case AttivitaStatus.CLOSED:
                  styleId = 'CheckInMarkerGreen';
                  break;
              }
            }

            if (styleId) {
              record['_markerFeature'] = this.mapService.addMarkerToTemporaryLayer(
                record.id,
                [record.position.longitude, record.position.latitude],
                MapService.EPSG_4326_KEY,
                styleId,
                {
                  id: record.id
                }
              );
            }
          }

          this._formatCollectionData(record);
        });

        if (resp.buffer && resp.path) {
          const format = new GeoJSON();

          const readOptions = {
            dataProjection: MapService.EPSG_4326_KEY,
            featureProjection: MapService.EPSG_3857_KEY
          };

          const writeOptions = {
            dataProjection: MapService.EPSG_3857_KEY,
            featureProjection: MapService.EPSG_3857_KEY
          };

          const geoPath4326 = format.readGeometry(resp.path.geometry, readOptions);
          const geoBuffer4326 = format.readGeometry(resp.buffer.geometry, readOptions);

          const geoPath3857 = format.writeGeometry(geoPath4326, writeOptions);
          const geoBuffer3857 = format.writeGeometry(geoBuffer4326, writeOptions);

          this.mapService.addFeatureToTemporaryLayer(geoBuffer3857, this._fwBufferFeatureStyle, false, false, 'fw_buffer');

          this.mapService.addFeatureToTemporaryLayer(geoPath3857, this._fwLinkFeatureStyle, false, false, 'fw_link');

          const geomExtent = format.readGeometry(geoBuffer3857).getExtent();

          this.mapService.openLayersMap.getView().fit(geomExtent);
        }

        // resp.records['name'] = resp.records['item_req'];

        this.dataGrid = resp.records;

        this.mapService.centerMapOnTempLayer();
      },
      error: (err) => {
        this.ebwModal.openErrorModal({
          title: this.i18NSrv.getInstantValue('common.error'),
          content: err?.error?.message ?? 'Errore recupero dati'
        });
      }
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  async centerMapMarker(element: any) {
    if (element._markerFeature) {
      this.mapService.centerMapOnGeometry(element._markerFeature.getGeometry(), true);
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  initializeTable(owner = false) {
    this.columnsGrid = [
      {
        header: 'Pos',
        field: 'position',
        width: '50px',
        cellTemplate: this.geometryColTemplate
      },
      {
        header: 'Info',
        field: 'info',
        sortable: false,
        cellTemplate: this.infoColTemplate
      },
      {
        header: 'Tipo Oggetto',
        field: 'name',
        sortable: false
      },
      {
        header: 'Id Oggetto',
        field: 'item_req',
        sortable: false,
        cellTemplate: this.idFeatureColTemplate
      },
      {
        header: 'Id Check-in',
        field: 'id',
        sortable: false,
        cellTemplate: this.idColTemplate
      },
      {
        header: 'Tipo',
        field: 'type',
        width: '100px',
        sortable: true,
        formatter: (data: any) => `${data.type == 'area' ? 'Area' : 'Punto'}`
      },
      {
        header: 'Stato',
        field: 'status',
        width: '100px',
        sortable: false,
        formatter: (data: any) =>
          `<span class="status-badge status-badge--${data.status}">${this.translateService.instant(
            'map.sidebar.checkInOut.state.' + data.status
          )}<span>`
      },
      {
        header: 'Check IN',
        field: 'date_time_checkin',
        sortable: true,
        formatter: (data: any) =>
          `<span class="date-field">${data.date_time_checkin ? moment(data.date_time_checkin).format('DD/MM/YYYY HH:mm:ss') : '-'}</span>`
      },
      {
        header: 'Check OUT',
        field: 'date_time_checkout',
        sortable: true,
        formatter: (data: any) =>
          `<span class="date-field">${data.date_time_checkout ? moment(data.date_time_checkout).format('DD/MM/YYYY HH:mm:ss  ') : '-'}</span>`
      },
      {
        header: 'Utente',
        field: 'user_name',
        sortable: true
      }
    ];

    if (owner) {
      this.columnsGrid.push({
        header: 'System',
        field: 'system',
        sortable: true
      });
      this.columnsGrid.push({
        header: 'Client',
        field: 'client_id',
        sortable: true
      });
    }
  }

  // ------------------------------------------------------------------------------------------------------------
  getFilters(limit = this.pageSize, offset = this.offset) {
    let nextFilters = {
      limit,
      offset
    };

    if (this._currentSort) {
      nextFilters['order_by'] = this._currentSort.field;
      nextFilters['order'] = this._currentSort.direction;
    }

    //Pagination is always applied
    let filters = this.config?.filters;

    //Apply filter
    if (filters && Object.keys(filters).length > 0) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key]) {
          return;
        }
        nextFilters[key] = filters[key];
      });
    }

    return nextFilters;
  }

  // ------------------------------------------------------------------------------------------------------------

  resetComponentProperties() {
    this.dataGrid = [];

    this.mapService.clearAllFeaturesFromTemporaryLayer();
  }

  /**
   * method for getting information about the feature specified in the row
   * @param row data about the current row of the table
   */
  getFeatureInfo(row: any) {
    // get the URL of the selectByUrns endpoint
    const selectByUrns = this._apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URN);

    // make an HTTP POST request to the selectByUrns Url passing the urn of the current feature
    this._utilsService.postRequest(selectByUrns, { urns: row.item_req }, false).subscribe({
      next: (response) => {
        // extract the feature
        const feature = new GeoJSON().readFeatures(response.result)[0];

        const { config_collection, config_enum, collection_name, collection_external_name } = this._featureSelectService.getConfigDataFromUrn(
          '' + feature.getId()
        );
        feature['urn'] = row.item_req;
        feature['collectionName_'] = collection_name;
        feature['collectionExternalName_'] = collection_external_name;
        feature['id'] = (feature.getId() as string).split('V')[4].split('Z')[1];

        this._featureSelectService.mapFeatureToModal(feature, 'view');
      },
      error: (error) => {
        // catch any error and display an error message
        this.ebwModal.openErrorModal({
          title: this.i18NSrv.getInstantValue('common.error'),
          content: error?.error?.message
        });
      }
    });
  }

  /**
   * method to export the current table data in different formats
   * @param type string to define the type of file to export
   */
  async onExport(type: string) {
    // prepare the variables for the http request
    let url, body, blobType;

    let dataUrl = this._apiUrlService.createUrl(API_URLS.CHECKIN.LIST);
    // Export delle sole righe che vedo
    // Per l'export massivo devono richiedere una CR per gestire tutto il giro completo con RabbitMQ
    let filters = this.getFilters(5000);

    try {
      const data: any = await firstValueFrom(this.httpClient.post(dataUrl, filters, { headers: this._headers }));
      if (!data) {
        this.ebwModal.openErrorModal({
          title: this.i18NSrv.getInstantValue('common.error'),
          content: ''
        });
        return;
      }

      data.records.forEach((record) => this._formatCollectionData(record));
      // setup the blobType, url and body depending on the type of file to export
      switch (type) {
        case 'csv':
          url = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.CSV) + '?filename=' + 'checkin-out-export';
          body = { data: this._formatExportData(data.records) };
          blobType = 'text/csv';
          break;

        case 'excel':
          url = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.EXCEL) + '?filename=' + 'checkin-out-export';
          body = { data: this._formatExportData(data.records, true) };
          blobType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;

        case 'pdf':
          url = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.PDF) + '?filename=' + 'checkin-out-export';
          body = { data: this._formatExportData(data.records) };
          blobType = 'application/pdf';
          break;
      }

      // set the response type as blob
      const opt = {
        responseType: 'blob'
      };

      // make the post HTTP request to the export endpoint to obtain the file to download
      this._utilsService.postRequest(url, body, false, opt, 'application/json').subscribe({
        next: (data: any) => {
          let downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(new Blob([data], { type: blobType }));
          downloadLink.setAttribute('download', 'checkin-out-export');
          document.body.appendChild(downloadLink);
          downloadLink.click();
        },
        error: (error: any) => {
          this.ebwModal.openErrorModal({
            title: this.i18NSrv.getInstantValue('common.error'),
            content: safeJsonStringify(error, String(error))
          });
        }
      });
    } catch (error) {
      this.ebwModal.openErrorModal({
        title: this.i18NSrv.getInstantValue('common.error'),
        content: safeJsonStringify(error?.message || this.i18NSrv.getInstantValue('common.data_retrieving_error'), '')
      });
    }
  }

  /**
   * method to format the table data for exporting
   * @param excel flag to return an object rather than an array of data
   * @returns
   */
  private _formatExportData(data: any[], excel: boolean = false) {
    const formattedData = [];

    // iterate throuh each row of the grid
    for (const row of data) {
      // prepare an object containing the formatted data of the current row
      const currentRow = {};
      // iterate through each column of the grid
      for (const col of this.columnsGrid) {
        // if the value is an object, write it as string of latitude and longitude
        if (typeof row[col.field] === 'object' && !Array.isArray(row[col.field]) && row[col.field] !== null) {
          currentRow[<string>col.header] = `${row[col.field].latitude} ${row[col.field].longitude}`;
          continue;
        }

        if (<string>col.header === 'Info') {
          continue;
        }

        // format the data correctly if it's the Check IN or Check OUT data
        if (<string>col.header === 'Check IN' || <string>col.header === 'Check OUT') {
          currentRow[<string>col.header] = row[col.field] ? moment(row[col.field]).format('DD/MM/YYYY HH:mm:ss') : '';
          continue;
        }

        // if the value is null, store it as an empty string
        if (!row[col.field]) {
          currentRow[<string>col.header] = '';
          continue;
        }
        // if the field is 'type', store it as 'Area' or 'Punto' depending on the value
        if (col.field === 'type') {
          currentRow[<string>col.header] = row[col.field] === 1 ? 'Area' : 'Punto';
          continue;
        }

        if (col.field === 'item_req') {
          currentRow[<string>col.header] = row[col.field].split('keysZ')[1];
          continue;
        }

        // store the value of the in the current row object
        currentRow[<string>col.header] = row[col.field];
      }
      // push the formatted row in the list of data
      formattedData.push(currentRow);
    }

    // if the excel flag is on, return an object instead
    if (excel) {
      return {
        query: formattedData
      };
    }

    // return the list of formatted data
    return formattedData;
  }

  /**
   * La tabella è un tool "passive" del click sulla mappa: la selezione sulla mappa evidenzia solo le righe, quindi cede a qualsiasi
   * altro strumento e riprende (onResume) quando l'altro lo rilascia. Se all'apertura c'è già uno strumento, si apre senza selezione.
   */
  private _acquireClickOwner() {
    const granted = this.mapService.acquireMapClick({
      id: this._clickOwnerId,
      level: 'passive',
      onYield: () => this._removeSelectFeatureClickIteration(),
      onResume: () => this._initSelectFeatureClickIteration()
    });
    if (granted) {
      this._initSelectFeatureClickIteration();
    }
  }

  private _initSelectFeatureClickIteration() {
    this._clickInteractionActive = true;
    this.mapService.customMapClickEnable$.next(true);
    this._selectClickInteraction = new Select({
      condition: click,
      style: null,
      multi: true
    });

    this._selectClickInteraction.on('select', (e) => {
      if (e.selected.length > 0) {
        this._onSelectedChekInOut(e.selected);
      }
    });

    this.mapService.addMapInteraction(this._selectClickInteractionKey, this._selectClickInteraction);
  }

  private _removeSelectFeatureClickIteration() {
    // se la selezione non è attiva (ceduta ad un altro strumento) il flag appartiene ad altri: non va toccato
    if (!this._clickInteractionActive) {
      return;
    }
    this._clickInteractionActive = false;
    this.mapService.customMapClickEnable$.next(false);
    this.mapService.removeMapInteraction(this._selectClickInteractionKey);
  }

  private _onSelectedChekInOut(features: Feature[]) {
    this.dataGrid = this.dataGrid.map((row) => ({ ...row, highlighted: false }));
    this.gridTable.detectChanges();

    const ids = features.map((el) => el.getProperties().id).filter(Boolean);

    if (!ids.length) {
      return;
    }

    this.dataGrid = this.dataGrid.map((row) => (ids.includes(row.id) ? { ...row, highlighted: true } : row));
    this.gridTable.detectChanges();
    setTimeout(() => {
      if (!this._isElementVisible('highlighted')) this._scrollToHighlightedDiv();
    }, 0);
  }

  private _scrollToHighlightedDiv() {
    const container = document.getElementsByClassName('mat-table-container');
    const targetElement: any = container[0].querySelector(`.highlighted`);

    if (targetElement) {
      container[0].scrollTo({
        top: targetElement.offsetTop - 100,
        behavior: 'auto'
      });
    }
  }

  private _isElementVisible(className: string): boolean {
    const container = document.getElementsByClassName('mat-table-container');
    const target = container[0].querySelector(`.${className}`);

    if (target) {
      const targetRect = target.getBoundingClientRect();
      const containerRect = container[0].getBoundingClientRect();

      return targetRect.top >= containerRect.top && targetRect.bottom <= containerRect.bottom;
    }
    return false;
  }

  private _formatCollectionData(record) {
    record['info'] = record['item_req'];

    if (record['item_req']) {
      let collectionName =
        record['item_req'].split('datasetZ')[1].split('VcollectionZ')[0] + '.' + record['item_req'].split('collectionZ')[1].split('VkeysZ')[0];

      let collection = this._collectionConfig.collections.find((collection) => {
        return collection.name == collectionName;
      });

      if (collection) {
        record['name'] = collection.external_name;
      }
    }
  }
}
